import { GrowthBook } from '@growthbook/growthbook';
import { backOff } from 'exponential-backoff';
import config from './env.config';
import { getAngularServiceOrUndefined } from '../modules/core';
const defaultAttrs = {
    id: undefined,
    anonymousId: undefined,
    institutionId: undefined,
    isFreeUser: false,
    isProUser: false,
    isClinicUser: false,
    institutionsMembershipsIds: undefined,
};
function makeAttributes(props) {
    const attrs = (props.previousAttrs ??
        window.growthbookInstance?.getAttributes() ??
        defaultAttrs);
    /**
     * Use `Object.prototype.hasOwnProperty.call(props, 'something')`
     * istead of `props.something` so that fields can be unset
     */
    if (props.hasOwnProperty('id')) {
        attrs.id = props.id;
    }
    if (props.hasOwnProperty('anonymousId')) {
        attrs.anonymousId = props.anonymousId;
    }
    if (props.hasOwnProperty('institutionId')) {
        attrs.institutionId = props.institutionId;
    }
    if (props.hasOwnProperty('subscriptionType')) {
        attrs.isFreeUser = props.subscriptionType === 'free';
        attrs.isProUser =
            props.subscriptionType === 'pro' || props.subscriptionType === 'trial';
        attrs.isClinicUser = props.subscriptionType === 'clinic';
    }
    if (props.hasOwnProperty('institutionsMembershipsIds')) {
        attrs.institutionsMembershipsIds = props.institutionsMembershipsIds;
    }
    return attrs;
}
function getFromLocalStorage(key) {
    return key && typeof window !== 'undefined'
        ? (localStorage.getItem(key) ?? '').replace(/^"|"$/gu, '')
        : '';
}
const localStorageSegmentAnonId = getFromLocalStorage('ajs_anonymous_id');
const localStorageSegmentId = getFromLocalStorage('ajs_id') ||
    getFromLocalStorage('ajs_user_id') ||
    localStorageSegmentAnonId;
const growthbook = new GrowthBook({
    enabled: true,
    attributes: makeAttributes({
        id: localStorageSegmentId,
        anonymousId: localStorageSegmentAnonId,
    }),
    trackingCallback: (experiment, result) => {
        const trackingFactory = getAngularServiceOrUndefined('trackingFactory');
        if (trackingFactory !== undefined) {
            trackingFactory.global.track('Experiment Viewed', {
                user_id: localStorageSegmentId || localStorageSegmentAnonId,
                experimentId: experiment.key,
                variationId: result.variationId,
            });
        }
    },
});
if (typeof window !== 'undefined') {
    window.growthbookInstance = growthbook;
}
async function loadFeatures() {
    // Load feature definitions from API
    const FEATURES_ENDPOINT = `https://cdn.growthbook.io/api/features/${config.growthbook.key}`;
    const res = await fetch(FEATURES_ENDPOINT, {
        method: 'GET',
        headers: {
            accept: 'application/json',
        },
    });
    if (!res.ok) {
        throw new Error(`Growthbook feature fetch error! status: ${res.status} - ${res.statusText}`);
    }
    const json = await res.json();
    growthbook.setFeatures(json.features);
}
let featuresPromise = undefined;
export async function getGrowthbookFeatures(forceReload = false) {
    if (!config.growthbook.key) {
        console.error('GROWTHBOOK IS NOT CONFIGURED PROPERLY');
        return featuresPromise;
    }
    if (!featuresPromise || forceReload) {
        featuresPromise = backOff(loadFeatures, {
            numOfAttempts: 5,
            retry(err, attemptNumber) {
                console.warn(`Failed to retrieve growthbook features: attempt ${attemptNumber}`, err);
                return true;
            },
        }).catch((err) => {
            console.error('Failed to retrieve growthbook features', err);
            featuresPromise = undefined;
        });
    }
    return featuresPromise;
}
export function mockGrowthbook(featureValues) {
    const features = Object.fromEntries(Object.entries(featureValues).map(([k, v]) => [k, { defaultValue: v }]));
    growthbook.setFeatures(features);
    return growthbook;
}
export function updateGrowthBookIndentifiers(props = {}) {
    if (window.hasOwnProperty('analytics')) {
        const input = {};
        if (props.institution?.id !== undefined) {
            input.institutionId = String(props.institution.id);
        }
        if (props.user) {
            input.id = String(props.user.id);
            const institutionsMembershipsIds = Array.isArray(props.user.institutionsMemberships)
                ? props.user.institutionsMemberships.map((institution) => String(institution.institutionId))
                : [];
            if (institutionsMembershipsIds.length > 0) {
                input.institutionsMembershipsIds = institutionsMembershipsIds;
            }
            input.subscriptionType = props.user.subscriptionType;
        }
        growthbook.setAttributes(makeAttributes(input));
    }
}
if (typeof window !== 'undefined') {
    updateGrowthBookIndentifiers();
}
export default growthbook;
